import React, { useState, useEffect, useCallback } from 'react';
import {
    ReactFlow,
    Background,
    Controls,
    MiniMap,
    useNodesState,
    useEdgesState,
    Handle,
    Position,
    MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';
import { Card, Button, Space, Spin, message, Tag, Tooltip, Modal, Descriptions, Badge } from 'antd';
import {
    ReloadOutlined,
    GlobalOutlined,
    ClusterOutlined,
    CloudServerOutlined,
    ExpandOutlined,
    CompressOutlined,
} from '@ant-design/icons';
import dnsService from '../../services/dnsService';

// Dagre布局配置
const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));

const getLayoutedElements = (nodes, edges, direction = 'TB') => {
    dagreGraph.setGraph({ rankdir: direction, nodesep: 80, ranksep: 120 });

    nodes.forEach((node) => {
        dagreGraph.setNode(node.id, { width: node.width || 180, height: node.height || 60 });
    });

    edges.forEach((edge) => {
        dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    const layoutedNodes = nodes.map((node) => {
        const nodeWithPosition = dagreGraph.node(node.id);
        return {
            ...node,
            position: {
                x: nodeWithPosition.x - (node.width || 180) / 2,
                y: nodeWithPosition.y - (node.height || 60) / 2,
            },
        };
    });

    return { nodes: layoutedNodes, edges };
};

// 自定义节点: 父节点组
const ParentGroupNode = ({ data }) => {
    return (
        <div
            style={{
                padding: '14px 18px',
                borderRadius: 10,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                minWidth: 180,
                boxShadow: '0 4px 14px rgba(102, 126, 234, 0.4)',
            }}
        >
            <Handle type="source" position={Position.Bottom} style={{ background: '#555' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <GlobalOutlined style={{ fontSize: 22 }} />
                <div>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{data.label}</div>
                    <div style={{ fontSize: 11, opacity: 0.9 }}>父节点组</div>
                </div>
            </div>
            {data.childCount !== undefined && (
                <div style={{ marginTop: 8, fontSize: 11, opacity: 0.8 }}>
                    {data.childCount} 子组
                </div>
            )}
        </div>
    );
};

// 自定义节点: 子节点组
const ChildGroupNode = ({ data }) => {
    const statusColor = data.status === 'active' ? '#52c41a' : '#ff4d4f';
    return (
        <div
            style={{
                padding: '12px 16px',
                borderRadius: 8,
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                minWidth: 170,
                boxShadow: '0 4px 12px rgba(240, 147, 251, 0.4)',
                cursor: 'pointer',
            }}
            onClick={() => data.onClick && data.onClick(data.raw)}
        >
            <Handle type="target" position={Position.Top} style={{ background: '#555' }} />
            <Handle type="source" position={Position.Bottom} style={{ background: '#555' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ClusterOutlined style={{ fontSize: 18 }} />
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{data.label}</div>
                    <div style={{ fontSize: 10, opacity: 0.9 }}>{data.nsHostname || '-'}</div>
                </div>
                <div
                    style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: statusColor,
                    }}
                />
            </div>
            {data.nodeCount !== undefined && (
                <div style={{ marginTop: 8, fontSize: 11, opacity: 0.8 }}>
                    {data.nodeCount} 节点
                </div>
            )}
        </div>
    );
};

// 自定义节点: DNS节点
const DnsNodeComponent = ({ data }) => {
    const statusColors = {
        active: '#52c41a',
        inactive: '#d9d9d9',
        maintenance: '#faad14',
    };
    const statusColor = statusColors[data.status] || '#d9d9d9';

    const typeColors = {
        dns_server: '#1890ff',
        dns_proxy: '#722ed1',
    };

    return (
        <div
            style={{
                padding: '10px 14px',
                borderRadius: 6,
                background: 'linear-gradient(135deg, #fa8231 0%, #f7b731 100%)',
                color: 'white',
                minWidth: 150,
                boxShadow: '0 4px 12px rgba(250, 130, 49, 0.4)',
                cursor: 'pointer',
            }}
            onClick={() => data.onClick && data.onClick(data.raw)}
        >
            <Handle type="target" position={Position.Top} style={{ background: '#555' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CloudServerOutlined style={{ fontSize: 16 }} />
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 12 }}>{data.label}</div>
                    <div style={{ fontSize: 10, opacity: 0.9 }}>{data.ip}</div>
                </div>
                <Tooltip title={data.status}>
                    <div
                        style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: statusColor,
                        }}
                    />
                </Tooltip>
            </div>
            {data.nodeType && (
                <div style={{ marginTop: 6 }}>
                    <Tag
                        style={{
                            fontSize: 10,
                            padding: '0 4px',
                            background: typeColors[data.nodeType] || '#999',
                            border: 'none',
                            color: 'white',
                        }}
                    >
                        {data.nodeType === 'dns_proxy' ? '代理' : '服务器'}
                    </Tag>
                </div>
            )}
        </div>
    );
};

const nodeTypes = {
    parentGroup: ParentGroupNode,
    childGroup: ChildGroupNode,
    dnsNode: DnsNodeComponent,
};

const Topology = () => {
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [loading, setLoading] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    const [selectedNode, setSelectedNode] = useState(null);
    const [detailModalVisible, setDetailModalVisible] = useState(false);

    // 原始数据
    const [rawData, setRawData] = useState({ parentGroups: [], childGroups: [], dnsNodes: [] });

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [parentRes, childRes, nodesRes] = await Promise.all([
                dnsService.getNodeParentGroups(),
                dnsService.getNodeChildGroups(),
                dnsService.getDnsNodes(),
            ]);

            const parentGroups = parentRes.success ? parentRes.data || [] : [];
            const childGroups = childRes.success ? childRes.data || [] : [];
            const dnsNodes = nodesRes.success ? nodesRes.data || [] : [];

            setRawData({ parentGroups, childGroups, dnsNodes });

            // 构建节点和边
            const flowNodes = [];
            const flowEdges = [];

            // 按父组分组子组
            const childByParent = {};
            childGroups.forEach((child) => {
                const parentId = child.parent_group_id || 'unassigned';
                if (!childByParent[parentId]) childByParent[parentId] = [];
                childByParent[parentId].push(child);
            });

            // 按子组分组DNS节点 (node_ids是数组)
            const nodesByChild = {};
            childGroups.forEach((child) => {
                if (child.node_ids && Array.isArray(child.node_ids)) {
                    nodesByChild[child.id] = dnsNodes.filter((n) => child.node_ids.includes(n.id));
                } else {
                    nodesByChild[child.id] = [];
                }
            });

            // 创建父节点组
            parentGroups.forEach((parent) => {
                const parentNodeId = `parent-${parent.id}`;
                const children = childByParent[parent.id] || [];

                flowNodes.push({
                    id: parentNodeId,
                    type: 'parentGroup',
                    data: {
                        label: parent.name,
                        childCount: children.length,
                        raw: parent,
                    },
                    position: { x: 0, y: 0 },
                    width: 200,
                    height: 80,
                });

                // 创建该父组下的子组
                children.forEach((child) => {
                    const childNodeId = `child-${child.id}`;
                    const childNodes = nodesByChild[child.id] || [];

                    flowNodes.push({
                        id: childNodeId,
                        type: 'childGroup',
                        data: {
                            label: child.name,
                            nsHostname: child.ns_hostname,
                            status: child.status,
                            nodeCount: childNodes.length,
                            raw: child,
                            onClick: handleChildClick,
                        },
                        position: { x: 0, y: 0 },
                        width: 180,
                        height: 80,
                    });

                    // 父组 -> 子组 边
                    flowEdges.push({
                        id: `edge-${parentNodeId}-${childNodeId}`,
                        source: parentNodeId,
                        target: childNodeId,
                        type: 'smoothstep',
                        animated: false,
                        style: { stroke: '#667eea', strokeWidth: 2 },
                        markerEnd: { type: MarkerType.ArrowClosed, color: '#667eea' },
                    });

                    // 创建该子组下的DNS节点
                    childNodes.forEach((dnsNode) => {
                        const dnsNodeId = `dns-${dnsNode.id}`;

                        // 检查是否已经添加过这个节点
                        if (!flowNodes.find((n) => n.id === dnsNodeId)) {
                            flowNodes.push({
                                id: dnsNodeId,
                                type: 'dnsNode',
                                data: {
                                    label: dnsNode.hostname,
                                    ip: dnsNode.ip_address,
                                    status: dnsNode.status,
                                    nodeType: dnsNode.node_type,
                                    raw: dnsNode,
                                    onClick: handleDnsNodeClick,
                                },
                                position: { x: 0, y: 0 },
                                width: 160,
                                height: 70,
                            });
                        }

                        // 子组 -> DNS节点 边
                        flowEdges.push({
                            id: `edge-${childNodeId}-${dnsNodeId}`,
                            source: childNodeId,
                            target: dnsNodeId,
                            type: 'smoothstep',
                            animated: false,
                            style: { stroke: '#f093fb', strokeWidth: 1.5 },
                            markerEnd: { type: MarkerType.ArrowClosed, color: '#f093fb' },
                        });
                    });
                });
            });

            // 处理未分配父组的子组
            const unassignedChildren = childByParent['unassigned'] || [];
            if (unassignedChildren.length > 0) {
                const unassignedParentId = 'parent-unassigned';
                flowNodes.push({
                    id: unassignedParentId,
                    type: 'parentGroup',
                    data: {
                        label: '未分配',
                        childCount: unassignedChildren.length,
                    },
                    position: { x: 0, y: 0 },
                    width: 200,
                    height: 80,
                });

                unassignedChildren.forEach((child) => {
                    const childNodeId = `child-${child.id}`;
                    const childNodes = nodesByChild[child.id] || [];

                    flowNodes.push({
                        id: childNodeId,
                        type: 'childGroup',
                        data: {
                            label: child.name,
                            nsHostname: child.ns_hostname,
                            status: child.status,
                            nodeCount: childNodes.length,
                            raw: child,
                            onClick: handleChildClick,
                        },
                        position: { x: 0, y: 0 },
                        width: 180,
                        height: 80,
                    });

                    flowEdges.push({
                        id: `edge-${unassignedParentId}-${childNodeId}`,
                        source: unassignedParentId,
                        target: childNodeId,
                        type: 'smoothstep',
                        animated: false,
                        style: { stroke: '#667eea', strokeWidth: 2 },
                        markerEnd: { type: MarkerType.ArrowClosed, color: '#667eea' },
                    });

                    childNodes.forEach((dnsNode) => {
                        const dnsNodeId = `dns-${dnsNode.id}`;

                        if (!flowNodes.find((n) => n.id === dnsNodeId)) {
                            flowNodes.push({
                                id: dnsNodeId,
                                type: 'dnsNode',
                                data: {
                                    label: dnsNode.hostname,
                                    ip: dnsNode.ip_address,
                                    status: dnsNode.status,
                                    nodeType: dnsNode.node_type,
                                    raw: dnsNode,
                                    onClick: handleDnsNodeClick,
                                },
                                position: { x: 0, y: 0 },
                                width: 160,
                                height: 70,
                            });
                        }

                        flowEdges.push({
                            id: `edge-${childNodeId}-${dnsNodeId}`,
                            source: childNodeId,
                            target: dnsNodeId,
                            type: 'smoothstep',
                            animated: false,
                            style: { stroke: '#f093fb', strokeWidth: 1.5 },
                            markerEnd: { type: MarkerType.ArrowClosed, color: '#f093fb' },
                        });
                    });
                });
            }

            // 显示未绑定到任何子组的节点
            const boundNodeIds = new Set();
            childGroups.forEach((child) => {
                if (child.node_ids) {
                    child.node_ids.forEach((id) => boundNodeIds.add(id));
                }
            });
            const unboundNodes = dnsNodes.filter((n) => !boundNodeIds.has(n.id));

            if (unboundNodes.length > 0) {
                const unboundGroupId = 'child-unbound';
                flowNodes.push({
                    id: unboundGroupId,
                    type: 'childGroup',
                    data: {
                        label: '未绑定节点',
                        nsHostname: '-',
                        status: 'active',
                        nodeCount: unboundNodes.length,
                    },
                    position: { x: 0, y: 0 },
                    width: 180,
                    height: 80,
                });

                unboundNodes.forEach((dnsNode) => {
                    const dnsNodeId = `dns-${dnsNode.id}`;

                    if (!flowNodes.find((n) => n.id === dnsNodeId)) {
                        flowNodes.push({
                            id: dnsNodeId,
                            type: 'dnsNode',
                            data: {
                                label: dnsNode.hostname,
                                ip: dnsNode.ip_address,
                                status: dnsNode.status,
                                nodeType: dnsNode.node_type,
                                raw: dnsNode,
                                onClick: handleDnsNodeClick,
                            },
                            position: { x: 0, y: 0 },
                            width: 160,
                            height: 70,
                        });
                    }

                    flowEdges.push({
                        id: `edge-${unboundGroupId}-${dnsNodeId}`,
                        source: unboundGroupId,
                        target: dnsNodeId,
                        type: 'smoothstep',
                        animated: true,
                        style: { stroke: '#ff7875', strokeWidth: 1.5, strokeDasharray: '5,5' },
                        markerEnd: { type: MarkerType.ArrowClosed, color: '#ff7875' },
                    });
                });
            }

            // 应用布局
            const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
                flowNodes,
                flowEdges,
                'TB'
            );

            setNodes(layoutedNodes);
            setEdges(layoutedEdges);
        } catch (error) {
            message.error('加载数据失败: ' + error.message);
        } finally {
            setLoading(false);
        }
    }, [setNodes, setEdges]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleChildClick = (childData) => {
        setSelectedNode({ type: 'child', data: childData });
        setDetailModalVisible(true);
    };

    const handleDnsNodeClick = (dnsNodeData) => {
        setSelectedNode({ type: 'dnsNode', data: dnsNodeData });
        setDetailModalVisible(true);
    };

    const toggleFullscreen = () => {
        setFullscreen(!fullscreen);
    };

    const containerStyle = fullscreen
        ? {
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1000,
              background: '#fff',
          }
        : { height: 'calc(100vh - 200px)', minHeight: 500 };

    return (
        <div>
            <Card
                title={
                    <Space>
                        <GlobalOutlined />
                        <span>DNS 拓扑视图</span>
                        <Tag color="blue">{rawData.parentGroups.length} 父组</Tag>
                        <Tag color="magenta">{rawData.childGroups.length} 子组</Tag>
                        <Tag color="orange">{rawData.dnsNodes.length} 节点</Tag>
                    </Space>
                }
                extra={
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
                            刷新
                        </Button>
                        <Button
                            icon={fullscreen ? <CompressOutlined /> : <ExpandOutlined />}
                            onClick={toggleFullscreen}
                        >
                            {fullscreen ? '退出全屏' : '全屏'}
                        </Button>
                    </Space>
                }
                bodyStyle={{ padding: 0 }}
            >
                <div style={containerStyle}>
                    {loading ? (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100%',
                            }}
                        >
                            <Spin size="large" tip="加载拓扑数据..." />
                        </div>
                    ) : (
                        <ReactFlow
                            nodes={nodes}
                            edges={edges}
                            onNodesChange={onNodesChange}
                            onEdgesChange={onEdgesChange}
                            nodeTypes={nodeTypes}
                            fitView
                            attributionPosition="bottom-left"
                        >
                            <Background color="#aaa" gap={16} />
                            <Controls />
                            <MiniMap
                                nodeColor={(node) => {
                                    switch (node.type) {
                                        case 'parentGroup':
                                            return '#667eea';
                                        case 'childGroup':
                                            return '#f093fb';
                                        case 'dnsNode':
                                            return '#fa8231';
                                        default:
                                            return '#eee';
                                    }
                                }}
                                maskColor="rgba(0, 0, 0, 0.1)"
                            />
                        </ReactFlow>
                    )}
                </div>
            </Card>

            {/* 详情弹窗 */}
            <Modal
                title={
                    selectedNode?.type === 'child'
                        ? `子节点组详情: ${selectedNode?.data?.name}`
                        : `DNS节点详情: ${selectedNode?.data?.hostname}`
                }
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={null}
                width={600}
            >
                {selectedNode?.type === 'child' && selectedNode?.data && (
                    <Descriptions bordered column={2}>
                        <Descriptions.Item label="ID">{selectedNode.data.id}</Descriptions.Item>
                        <Descriptions.Item label="名称">{selectedNode.data.name}</Descriptions.Item>
                        <Descriptions.Item label="NS主机名">
                            {selectedNode.data.ns_hostname || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="状态">
                            <Badge
                                status={
                                    selectedNode.data.status === 'active' ? 'success' : 'default'
                                }
                                text={selectedNode.data.status === 'active' ? '活跃' : '未激活'}
                            />
                        </Descriptions.Item>
                        <Descriptions.Item label="父组ID">
                            {selectedNode.data.parent_group_id || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="绑定节点数">
                            {selectedNode.data.node_ids?.length || 0}
                        </Descriptions.Item>
                    </Descriptions>
                )}
                {selectedNode?.type === 'dnsNode' && selectedNode?.data && (
                    <Descriptions bordered column={2}>
                        <Descriptions.Item label="ID">{selectedNode.data.id}</Descriptions.Item>
                        <Descriptions.Item label="主机名">
                            {selectedNode.data.hostname}
                        </Descriptions.Item>
                        <Descriptions.Item label="IP地址">
                            {selectedNode.data.ip_address || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="状态">
                            <Badge
                                status={
                                    selectedNode.data.status === 'active'
                                        ? 'success'
                                        : selectedNode.data.status === 'maintenance'
                                        ? 'warning'
                                        : 'default'
                                }
                                text={
                                    selectedNode.data.status === 'active'
                                        ? '活跃'
                                        : selectedNode.data.status === 'maintenance'
                                        ? '维护中'
                                        : '未激活'
                                }
                            />
                        </Descriptions.Item>
                        <Descriptions.Item label="节点类型">
                            <Tag
                                color={
                                    selectedNode.data.node_type === 'dns_proxy'
                                        ? 'purple'
                                        : 'blue'
                                }
                            >
                                {selectedNode.data.node_type === 'dns_proxy'
                                    ? 'DNS代理'
                                    : 'DNS服务器'}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="端口">
                            {selectedNode.data.port || 53}
                        </Descriptions.Item>
                        <Descriptions.Item label="上次同步">
                            {selectedNode.data.last_sync
                                ? new Date(selectedNode.data.last_sync).toLocaleString()
                                : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="区域数">
                            {selectedNode.data.zone_count || 0}
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>
        </div>
    );
};

export default Topology;
